import {SelectionService} from '@frontapp/editor-html';
import {useCallback, useEffect, useState, useSyncExternalStore} from 'react';
import {
  animationFrameScheduler,
  BehaviorSubject,
  combineLatest,
  fromEvent,
  MonoTypeOperatorFunction,
  Observable,
  of,
  partition,
  timer,
} from 'rxjs';
import {
  debounce,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  publish,
  scan,
  skip,
  skipUntil,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import {findAncestorByTag, isElement} from '../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {isNotNull} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {isElementSelectionService} from '../../../../helpers/editorHelpers';
import {
  FormattingPopoverInteractionStatesEnum,
  fromFormattingPopoverInteractionState,
} from './formattingPopoverInteraction';

/*
 * Types.
 */

export enum FormattingPopoverAnchorTypesEnum {
  SELECTION = 'SELECTION',
  LINK = 'LINK',
}

interface FormattingPopoverAnchorBase {
  type: FormattingPopoverAnchorTypesEnum;
  bounds: DOMRect;
}

export interface FormattingPopoverSelectionAnchor extends FormattingPopoverAnchorBase {
  type: FormattingPopoverAnchorTypesEnum.SELECTION;
}

export interface FormattingPopoverLinkAnchor extends FormattingPopoverAnchorBase {
  type: FormattingPopoverAnchorTypesEnum.LINK;
  linkElement: HTMLAnchorElement;
}

export type FormattingPopoverAnchor = FormattingPopoverSelectionAnchor | FormattingPopoverLinkAnchor;

/*
 * Factories.
 */

function createSelectionAnchor(range: Range): FormattingPopoverSelectionAnchor {
  return {
    type: FormattingPopoverAnchorTypesEnum.SELECTION,
    bounds: range.getBoundingClientRect(),
  };
}

function createLinkAnchor(linkElement: HTMLAnchorElement): FormattingPopoverLinkAnchor {
  return {
    type: FormattingPopoverAnchorTypesEnum.LINK,
    bounds: linkElement.getBoundingClientRect(),
    linkElement,
  };
}

/*
 * API.
 */

// Used as a stable callback.
const createAnchorState = () => new BehaviorSubject<FormattingPopoverAnchor | null>(null);

export function useFormattingPopoverAnchor(
  selectionService: SelectionService,
  editorElement$: Observable<HTMLElement | null>,
  popoverContentElement$: Observable<HTMLElement | null>,
  shouldIgnoreBlurredState$: Observable<boolean>,
  dissmissAnchorOnScrollClassNames: ReadonlyArray<string>,
): [FormattingPopoverAnchor | null, () => void] {
  // Create a behavior subject, and allow the popover to be dismissed by starting from scratch.
  const [anchor$, dismissAnchor] = useResettableState(createAnchorState);

  // Starts/stops the feed of new anchors into the behavior subject.
  useEffect(() => {
    const subscription = fromFormattingPopoverAnchor(
      selectionService,
      editorElement$,
      popoverContentElement$,
      shouldIgnoreBlurredState$,
      dissmissAnchorOnScrollClassNames,
    ).subscribe(anchor$);

    return () => subscription.unsubscribe();
  }, [
    anchor$,
    selectionService,
    editorElement$,
    popoverContentElement$,
    shouldIgnoreBlurredState$,
    dissmissAnchorOnScrollClassNames,
  ]);

  const anchor = useBehaviorSubject(anchor$);

  return [anchor, dismissAnchor];
}

/*
 * Helpers.
 */

function useResettableState<T>(stateFactory: () => T): [T, () => void] {
  const [state, setState] = useState(() => stateFactory());
  const resetState = useCallback(() => setState(stateFactory()), [setState, stateFactory]);
  return [state, resetState];
}

function useBehaviorSubject<T>(value$: BehaviorSubject<T>): T {
  const subscribe = useCallback(
    (callback: () => void) => {
      const subscription = value$.pipe(skip(1), debounceTime(0, animationFrameScheduler)).subscribe(callback);

      return () => subscription.unsubscribe();
    },
    [value$],
  );

  return useSyncExternalStore(subscribe, () => value$.getValue());
}

/** Emits the "popover anchor" when it changes, and only once the UI has settled. */
function fromFormattingPopoverAnchor(
  selectionService: SelectionService,
  editorElement$: Observable<HTMLElement | null>,
  popoverContentElement$: Observable<HTMLElement | null>,
  shouldIgnoreBlurredState$: Observable<boolean>,
  dissmissAnchorOnScrollClassNames: ReadonlyArray<string>,
): Observable<FormattingPopoverAnchor | null> {
  const selectionAnchor$ = fromFormattingPopoverSelectionAnchor(selectionService);
  const linkAnchor$ = fromFormattingPopoverLinkAnchor(editorElement$);

  // Emits potential popover anchors, when subscribed to (assuming input observables do) and when it changes.
  const anchor$ = combineLatest([selectionAnchor$, linkAnchor$]).pipe(
    // Prefer the selection anchor over the link hover anchor.
    map(([selectionAnchor, linkAnchor]) => selectionAnchor || linkAnchor),
  );

  // Emits previous/next pairs of potential popover anchors, starting with a [null, /* first anchor */] pair.
  const anchorChange$ = anchor$.pipe(startWith(null), pairwise());

  // Tracks the UI state of the editor and the popover.
  return fromFormattingPopoverInteractionState(
    editorElement$,
    popoverContentElement$,
    dissmissAnchorOnScrollClassNames,
  ).pipe(
    // Multicast to share underlying event handlers for each final subscriber.
    publish((state$): Observable<FormattingPopoverSelectionAnchor | FormattingPopoverLinkAnchor | null> => {
      // Emits potential popover anchors, except if to avoid changing the popover while the user is interacting with it.
      const stableAnchor$ = anchorChange$.pipe(
        withLatestFrom(state$),
        filter(([[prevAnchor, nextAnchor], state]) => !shouldSkipAnchorChange(state, prevAnchor, nextAnchor)),
        map(([[, nextAnchor]]) => nextAnchor),
      );

      // Emits actual popover anchors when the state changes or when the potential anchor changes.
      return combineLatest([
        state$,
        stableAnchor$,
        shouldIgnoreBlurredState$.pipe(
          scan((ignoreCount, shouldIgnore) => (shouldIgnore ? ignoreCount + 1 : ignoreCount - 1), 0),
          startWith(0),
        ),
      ]).pipe(
        map(([state, anchor, ignoreCount]) => {
          if (shouldHidePopover(state, ignoreCount > 0)) {
            return null;
          }

          // Otherwise, emit the last known anchor.
          return anchor;
        }),
      );
    }),
    // Filter out duplicate emissions from state changes that result in the same anchor.
    distinctUntilChanged(),
    // Skip the initial emission + any emissions that are asynchronous side-effects from past changes.
    // (Example: the browser re-focusing the editor after dismissing a popover).
    skipUntil(timer(0)),
  );
}

function shouldSkipAnchorChange(
  state: FormattingPopoverInteractionStatesEnum,
  prevAnchor: FormattingPopoverAnchor | null,
  nextAnchor: FormattingPopoverAnchor | null,
): boolean {
  // If the popover has focus, avoid changing the popover while editing a link URL.
  if (state === FormattingPopoverInteractionStatesEnum.POPOVER_FOCUSED) {
    return true;
  }

  // If the popover has hover, avoid insignificant changes to avoid shifting it when bolding/unbolding.
  if (state === FormattingPopoverInteractionStatesEnum.POPOVER_HOVERING) {
    return prevAnchor?.type === nextAnchor?.type;
  }

  return false;
}

function shouldHidePopover(
  state: FormattingPopoverInteractionStatesEnum,
  shouldIgnoreBlurredState?: boolean,
) {
  if (shouldIgnoreBlurredState) {
    return (
      state === FormattingPopoverInteractionStatesEnum.EDITOR_TYPING ||
      state === FormattingPopoverInteractionStatesEnum.EDITOR_SELECTING_WITH_MOUSE ||
      state === FormattingPopoverInteractionStatesEnum.EDITOR_SCROLLING
    );
  }
  return (
    state === FormattingPopoverInteractionStatesEnum.BLURRED ||
    state === FormattingPopoverInteractionStatesEnum.EDITOR_TYPING ||
    state === FormattingPopoverInteractionStatesEnum.EDITOR_SELECTING_WITH_MOUSE ||
    state === FormattingPopoverInteractionStatesEnum.EDITOR_SCROLLING
  );
}

/** Emits whether we want to show or hide a selection popover, when subscribed to and when it changes. */
function fromFormattingPopoverSelectionAnchor(
  selectionService: SelectionService,
): Observable<FormattingPopoverSelectionAnchor | null> {
  return selectionService.changes$.pipe(
    startWith(null),
    map(() => {
      // TODO: Ship an editor-html package update so we don't have to do this.
      // Note: This changes dynamically, because selectionService is a Proxy!
      if (!isElementSelectionService(selectionService)) {
        return null;
      }

      // Check whether we have a non-collapsed/non-empty selection.
      const range = selectionService.selection?.range;
      if (!range || range.collapsed) {
        return null;
      }

      return createSelectionAnchor(range);
    }),
    distinctUntilChanged(),
  );
}

const hoverIntentEnterTimeoutInMs = 500;
const hoverIntentLeaveTimeoutInMs = 50;

/** Emits whether we want to show or hide a link popover, when subscribed to (assuming the input observable does) and when it changes. */
function fromFormattingPopoverLinkAnchor(
  editorElement$: Observable<Element | null>,
): Observable<FormattingPopoverLinkAnchor | null> {
  return editorElement$.pipe(
    switchMap((element) => {
      if (!element) {
        return of(null);
      }

      return fromLinkUnderMouse(element).pipe(
        // Make the link popover appear/disapper with a delay, similar to a tooltip.
        applyHoverIntent(isNotNull, hoverIntentEnterTimeoutInMs, hoverIntentLeaveTimeoutInMs),
        // Assume we're not hovering a link initially.
        startWith(null),
      );
    }),
    map((linkElement) => {
      if (!linkElement) {
        return null;
      }

      return createLinkAnchor(linkElement);
    }),
    distinctUntilChanged(),
  );
}

/** Emits when the link element (if any) under the mouse pointer changes. */
function fromLinkUnderMouse(element: Element): Observable<HTMLAnchorElement | null> {
  return fromEvent<MouseEvent>(element, 'mouseover').pipe(
    map((event) => {
      const {target} = event;
      return isElement(target) ? findAncestorByTag(target, element, 'a') : null;
    }),
    // De-dupe events bubbling up from multiple children.
    distinctUntilChanged(),
  );
}

/** Delays the initial emission of truthy values, and then delays the initial emission of falsy values. */
function applyHoverIntent<T>(
  hoverPredicate: (value: T) => boolean,
  enterTimeoutInMs: number,
  leaveTimeoutInMs: number,
): MonoTypeOperatorFunction<T> {
  return publish((source$) => {
    const [enterValue$, leaveValue$] = partition(source$, hoverPredicate);
    const isHovering$ = source$.pipe(
      map(hoverPredicate),
      distinctUntilChanged(),
      debounce((isHovering) => (isHovering ? of(undefined) : timer(leaveTimeoutInMs))),
      debounce((isHovering) => (isHovering ? timer(enterTimeoutInMs) : of(undefined))),
    );

    return combineLatest([isHovering$, enterValue$, leaveValue$]).pipe(
      map(([isHovering, enterValue, leaveValue]) => (isHovering ? enterValue : leaveValue)),
    );
  });
}
